/* eslint-disable no-restricted-imports */
import clsx from 'clsx'
import React, {type ReactNode} from 'react'
import styles from './UIGameCard.module.scss'
import {type CardEnum, PlayerColor} from '@colonist/game-states'
import {T, Tooltip} from '@colonist/ui'
import {UIGameCount, type UIGameCountSize} from '../UIGameCount/UIGameCount.tsx'
import {type Placement} from '@floating-ui/react'
import {CARD_IMAGES, CARD_TIP_BODY, CARD_TIP_TITLE} from '@colonist/ui-game'
import {type TranslationObject} from '@colonist/i18n'
import {UIGameFloatingText} from '../UIGameFloatingText/UIGameFloatingText.tsx'
import {useUIGameTranslationContext} from '../../contexts/UIGameTranslationContext.tsx'

const CountBadge = ({count, size}: { count: number, size: UIGameCountSize }) => (
    <UIGameCount
        className={styles.countBadge}
        color={PlayerColor.Blue}
        count={count}
        size={size}
    />
)

const RatioBanner = ({tradeRatio}: {tradeRatio: number}) => (
    <div className={styles.ratioBanner}>
        <T id="strings:colonistCommon.utils.#buffer" options={{value: `${tradeRatio}:1`}} />
    </div>
)

interface CardContentProps {
    className: string
    cardEnum: CardEnum
    onClick: VoidFunction | undefined
    count: number | undefined
    tradeRatio?: number | undefined
    isDisabled?: boolean | undefined
    isGreyedOut?: boolean | undefined
    highlightAnimation?: boolean | undefined
    bounceAnimation?: boolean | undefined
    size: 'xxxs' | 'xxs' | 'xs' | 'sm' | 'base'
}

const CardContent = ({
    className,
    cardEnum,
    onClick,
    count,
    tradeRatio,
    isDisabled,
    isGreyedOut,
    highlightAnimation,
    size,
    bounceAnimation,
}: CardContentProps) => (
    <div
        className={clsx(styles.cardContainer, styles[size],
            {
                [styles.clickable]: onClick != undefined,
                [styles.isDisabled]: isDisabled,
                [styles.highlight]: highlightAnimation,
                [styles.bounce]: bounceAnimation,
            },
            className)}
        onClick={onClick}
        data-card-enum={cardEnum}
    >
        <img
            draggable={false}
            className={clsx(styles.cardImage, {
                [styles.isGreyedOut]: isGreyedOut,
            })}
            src={CARD_IMAGES[cardEnum]}
        />
        {count != undefined && <CountBadge count={count} size={size} />}
        {tradeRatio != undefined && <RatioBanner tradeRatio={tradeRatio} />}
    </div>
)

export interface CustomTooltip {
    title?: ReactNode
    content?: ReactNode
    centerText?: boolean
}

export interface UIGameCardProps {
    cardEnum: CardEnum
    className?: string
    onClick?: (cardEnum: CardEnum) => void
    showTipTitle?: boolean
    showTipBody?: boolean
    count?: number
    tooltipPosition?: Placement
    tooltipClassName?: string | undefined
    tradeRatio?: number | undefined
    isDisabled?: boolean | undefined
    isGreyedOut?: boolean | undefined
    highlightAnimation?: boolean | undefined
    bounceAnimation?: boolean | undefined
    helperText?: TranslationObject | undefined
    size?: 'xxxs' | 'xxs' | 'xs' | 'sm' | 'base'
    customTooltip?: CustomTooltip | undefined
}

const UIGameCardComponent = ({
    className = '',
    cardEnum,
    showTipTitle = false,
    showTipBody = false,
    onClick,
    count = undefined,
    tooltipPosition = 'top',
    tradeRatio,
    isDisabled,
    isGreyedOut,
    highlightAnimation,
    bounceAnimation,
    helperText,
    size = 'base',
    customTooltip,
}: UIGameCardProps) => {
    const {translationKeyContext} = useUIGameTranslationContext()
    const handleClick: VoidFunction | undefined = onClick ? () => onClick(cardEnum) : undefined
    const hasHelperText = helperText != null
    const hasCustomTooltip = customTooltip != null
    const hasTooltip = hasCustomTooltip || showTipTitle || showTipBody
    const tipBody: TranslationObject = {
        ...CARD_TIP_BODY[cardEnum],
        options: {context: translationKeyContext},
    }
    const tipTitle: TranslationObject = {
        ...CARD_TIP_TITLE[cardEnum],
        options: {context: translationKeyContext},
    }
    const cardContent = (
        <CardContent
            className={className}
            count={count}
            cardEnum={cardEnum}
            onClick={handleClick}
            tradeRatio={tradeRatio}
            isDisabled={isDisabled}
            isGreyedOut={isGreyedOut}
            highlightAnimation={highlightAnimation}
            size={size}
            bounceAnimation={bounceAnimation}
        />
    )

    if(hasCustomTooltip) {
        return <Tooltip
            centerText={customTooltip.centerText}
            content={customTooltip.content}
            menuClassName={styles.tooltipMenu}
            position={tooltipPosition}
            title={customTooltip.title}>
            {hasHelperText ? (
                <UIGameFloatingText direction="up" text={helperText}>
                    {cardContent}
                </UIGameFloatingText>
            ) : cardContent}
        </Tooltip>
    }

    if(hasTooltip && hasHelperText) {
        return (<Tooltip
            centerText={false}
            content={showTipBody ? <T id={tipBody}/> : undefined}
            menuClassName={styles.tooltipMenu}
            position={tooltipPosition}
            title={showTipTitle ? <T id={tipTitle}/> : undefined}>
            <UIGameFloatingText direction="up" text={helperText}>
                {cardContent}
            </UIGameFloatingText>
        </Tooltip>
        )
    }

    if(hasTooltip) {
        return <Tooltip
            centerText={false}
            content={showTipBody ? <T id={tipBody}/> : undefined}
            menuClassName={styles.tooltipMenu}
            position={tooltipPosition}
            title={showTipTitle ? <T id={tipTitle}/> : undefined}>
            {cardContent}
        </Tooltip>
    }

    if(hasHelperText) {
        return <UIGameFloatingText direction="up" text={helperText} >
            {cardContent}
        </UIGameFloatingText>
    }
    return cardContent
}

export const UIGameCard = React.memo(UIGameCardComponent)
